<!--
  =====================================================================
  PAGE SUBMENU - NAVIGATION DROPDOWN
  =====================================================================
  
  Fixed navigation submenu component for multi-section pages.
  
  FEATURES:
  - Responsive dropdown menu with hover/click interactions
  - Smooth scroll to section anchors with offset
  - Active state tracking for current selection
  - Keyboard accessible with proper ARIA labels
  - Auto-detects label source from sections data
  
  BEHAVIOR:
  - Main label: Always displays on left side
  - Dropdown: Shows on hover (desktop) or click (mobile)
  - Click outside: Closes dropdown automatically
  - Scroll behavior: Smooth scroll with 40px offset
  
  =====================================================================
-->

<template>
  <div
    class="page-submenu-wrapper"
    :style="{ top: `var(--header-total-height)` }"
  >
    <nav class="page-submenu">
      <div class="page-submenu__container">
        <span class="page-submenu__main">{{ mainLabel }}</span>

        <template v-if="menu.length">
          <span class="page-submenu__separator">/</span>

          <div 
            class="page-submenu__select" 
            ref="selectWrapper"
            @mouseenter="openDropdown"
            @mouseleave="closeHover"
          >
            <button
              class="page-submenu__toggle"
              @click="toggleDropdown"
              type="button"
              :aria-expanded="isOpen"
            >
              {{ current }}
              <span class="arrow" :class="{ 'arrow-open': isOpen }">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>

            <ul
              v-if="isOpen"
              class="page-submenu__dropdown"
              role="menu"
            >
              <li v-for="(item, i) in menu" :key="i" role="none">
                <button
                  type="button"
                  role="menuitem"
                  @click="go(item)"
                >
                  {{ item.label }}
                </button>
              </li>
            </ul>
          </div>
        </template>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'

// ───────────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {String} mainLabel - Label to display on the left side
 * @prop {Array} sections - Array of section objects with content/title
 * @prop {String} initialSection - Initially selected section label
 * @prop {String} labelKey - Property name to extract label from content
 * @prop {Boolean} overrideExisting - Whether to override existing target attributes
 */
const props = defineProps({
  mainLabel: String,
  sections: { type: Array, default: () => [] },
  initialSection: String,
  labelKey: { type: String, default: 'title' },
  overrideExisting: { type: Boolean, default: true }
})

// ───────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ───────────────────────────────────────────────────────────────────

/**
 * Converts text to URL-friendly slug format
 * Removes accents, converts to lowercase, replaces spaces/special chars with hyphens
 * @param {String} str - Text to slugify
 * @returns {String} Slugified text
 */
function slugify (str = '') {
  return String(str)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES - MENU BUILDING
// ───────────────────────────────────────────────────────────────────

/**
 * Builds menu items from sections data
 * Extracts label from content or fallback properties
 * Generates anchor targets from slugified labels
 */
const menu = computed(() =>
  props.sections
    .map(s => {
      const labelFromContent = s?.content?.[props.labelKey];
      const labelCandidate =
        labelFromContent ??
        s?.[props.labelKey] ??
        s?.label ??
        '';

      if (!labelCandidate) return null;

      if (!props.overrideExisting && s?.label && s?.target) {
        const target = String(s.target).startsWith('#') ? s.target : ('#' + s.target);
        return { label: s.label, target };
      }

      return {
        label: labelCandidate,
        target: '#' + slugify(labelCandidate)
      };
    })
    .filter(Boolean)
);

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE - DROPDOWN & SELECTION
// ───────────────────────────────────────────────────────────────────

const isHovering = ref(false);
const isClickOpen = ref(false);
const isOpen = computed(() => isHovering.value || isClickOpen.value);

const current = ref(props.initialSection || menu.value[0]?.label || '');
const selectWrapper = ref(null);

// ───────────────────────────────────────────────────────────────────
// WATCHERS
// ───────────────────────────────────────────────────────────────────

/**
 * Updates current selection when menu changes
 * Ensures selected item still exists in new menu
 */
watch(menu, (m) => {
  if (!m.length) return;
  if (!m.some(v => v.label === current.value)) {
    current.value = m[0].label;
  }
});

// ───────────────────────────────────────────────────────────────────
// EVENT HANDLERS - DROPDOWN INTERACTION
// ───────────────────────────────────────────────────────────────────

/**
 * Opens dropdown on hover
 */
const openDropdown = () => { isHovering.value = true; };

/**
 * Closes dropdown on mouse leave
 */
const closeHover = () => { isHovering.value = false; };

/**
 * Toggles dropdown open/closed state on click
 */
const toggleDropdown = () => {
  if (isOpen.value && !isClickOpen.value) {
    isClickOpen.value = true;
  } else {
    isClickOpen.value = !isClickOpen.value;
  }
};

// ───────────────────────────────────────────────────────────────────
// NAVIGATION LOGIC
// ───────────────────────────────────────────────────────────────────

/**
 * Navigates to section target with smooth scroll and offset
 * Updates current selection and closes dropdown
 * @param {Object} item - Menu item with label and target properties
 */
function go(item) {
  isHovering.value = false;
  isClickOpen.value = false;
  current.value = item.label;
  setTimeout(() => {
    const el = document.querySelector(item.target);
    if (el) {
      const style = getComputedStyle(document.documentElement);
      const headerHeight = parseFloat(style.getPropertyValue('--header-total-height')) || 65;
      const submenuHeight = parseFloat(style.getPropertyValue('--page-submenu-height')) || 60;
      const offset = headerHeight + submenuHeight + 16;
      const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, 100);
}

// ───────────────────────────────────────────────────────────────────
// CLICK OUTSIDE HANDLER
// ───────────────────────────────────────────────────────────────────

/**
 * Closes dropdown when clicking outside the menu
 * Uses composedPath for shadow DOM compatibility
 * @param {Event} e - Click event
 */
function handleClickOutside(e) {
  const path = e.composedPath ? e.composedPath() : [];
  const inside = selectWrapper.value && (path.includes(selectWrapper.value) || selectWrapper.value.contains(e.target));
  if (isOpen.value && !inside) {
    isHovering.value = false;
    isClickOpen.value = false;
  }
}

// ───────────────────────────────────────────────────────────────────
// SCROLL SPY - DETECT VISIBLE SECTION
// ───────────────────────────────────────────────────────────────────

/**
 * Calculates the visible height of an element in the viewport
 * @param {Element} el - DOM element to measure
 * @returns {Number} Height of element visible in viewport (0 if not visible)
 */
function getVisibleHeight(el) {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  
  // Element is below or above viewport
  if (rect.bottom < 0 || rect.top > viewportHeight) return 0;
  
  // Calculate visible portion
  const top = Math.max(0, rect.top);
  const bottom = Math.min(viewportHeight, rect.bottom);
  return bottom - top;
}

/**
 * Updates current section based on which section occupies most viewport space
 */
function updateActiveSection() {
  let maxVisibleHeight = 0;
  let activeLabel = current.value;
  
  menu.value.forEach(item => {
    const el = document.querySelector(item.target);
    const visibleHeight = getVisibleHeight(el);
    
    if (visibleHeight > maxVisibleHeight) {
      maxVisibleHeight = visibleHeight;
      activeLabel = item.label;
    }
  });
  
  if (maxVisibleHeight > 0) {
    current.value = activeLabel;
  }
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('scroll', updateActiveSection);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('scroll', updateActiveSection);
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

/* ------------------------------------------------------------------
   Wrapper - Fixed positioning below header
------------------------------------------------------------------ */
.page-submenu-wrapper {
  position: fixed;
  left: 0;
  width: 100%;
  z-index: 120;
}

.page-submenu {
  width: 100%;
  padding: $padding-default;
  padding-top: 1.2rem !important;
  padding-bottom: 0.5rem !important;
  background: $clr-bg-page;
}

/* ------------------------------------------------------------------
   Container - Main label and dropdown
------------------------------------------------------------------ */
.page-submenu__container {
  display: flex;
  align-items: center;
  @include flex-gap(0.75rem);
  flex-wrap: wrap;
  font-size: clamp(0.95rem, 2.5vw, 1.15rem);
  margin-left: 1rem;
}

.page-submenu__main      { font-weight: 700; color: $text-color; }
.page-submenu__separator { color: $clr-tertiary; font-size: clamp(1.2rem, 3vw, 1.5rem); font-weight: 600; }

/* ------------------------------------------------------------------
   Select wrapper and toggle button
------------------------------------------------------------------ */
.page-submenu__select  { position: relative; }

.page-submenu__toggle  {
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  @include flex-gap(0.5rem);
  font-weight: 500;
  color: $text-color;
  font-size: inherit;
}

.arrow { 
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.2em;
  height: 1.2em;
  color: $clr-tertiary;
  transition: transform 0.3s ease;

  svg {
    width: 100%;
    height: 100%;
  }

  &.arrow-open {
    transform: rotate(180deg);
  }
}

/* ------------------------------------------------------------------
   Dropdown menu - Desktop styling
------------------------------------------------------------------ */
$page-dropdown-gap: 1rem;

.page-submenu__dropdown {
  position: absolute;
  top: 100%;
  margin-top: $page-dropdown-gap;
  left: 0;
  background: #fff;
  border: 1px solid $border-color;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  list-style: none;
  padding: 0.5rem 1rem;
  min-width: 12rem;
  width: max-content;
  z-index: 150;

  li { padding: 0.5rem 1rem; }

  button {
    all: unset;
    cursor: pointer;
    display: block;
    width: 100%;
    color: $text-color;
    white-space: nowrap;
    font-size: clamp(0.9rem, 2.3vw, 1.05rem);

    @media (hover: hover) {
      &:hover { color: $clr-tertiary; }
    }
  }
}

/* ------------------------------------------------------------------
   Mobile responsive - Tablet and smaller
------------------------------------------------------------------ */
@media (max-width: 768px) {
  .page-submenu__container {
    flex-wrap: nowrap;
    @include flex-gap(0.5rem);
    font-size: clamp(0.85rem, 3.5vw, 1rem);
    margin-left: 0;
  }

  .page-submenu__main,
  .page-submenu__separator { white-space: nowrap; }

  .page-submenu__select {
    flex: 1 1 auto;
    position: relative;
  }

  .page-submenu__toggle {
    justify-content: space-between;
    width: 100%;
    padding: 0.6rem 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .page-submenu__dropdown {
    position: absolute;
    top: 100%;
    margin-top: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 90vw;
    max-height: 60vh;
    overflow-y: auto;
    border: 1px solid $border-color;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    background: #fff;
    padding: 0;

    li { padding: 0.8rem 1rem; }

    button {
      padding: 0;
      text-align: left;
    }
  }
}
</style>